"use client";

import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { scoreInk } from "@/lib/scoreCalibration";
import type { PreviewCapture } from "@/lib/previewCapture";

/* ────────────────────────────────────────────────────────────────────────────
 *  MeasuredTimeline — the series behind one score, drawn on a year axis.
 *
 *  This is the page's answer to "why is this more than asking a chatbot or
 *  looking it up myself": several of the numbers in the score are not facts
 *  anyone wrote down but MEASUREMENTS that took years to accumulate — twelve
 *  yearly WOZ assessments of this unit, thirty-one years of Amsterdam sale
 *  prices, nineteen years of satellite irradiance, four years of radar
 *  passes over this block, five years of police counts. Each bar spans the
 *  observation window the product actually read for this address, and every
 *  span, count and score is read off the capture sidecar (§Marketing claims
 *  — nothing here is typed). A row is skipped when the capture lacks it.
 *
 *  Solid = measured; hatched = the part of a window that lies beyond the
 *  capture date (a label's validity, a forecast). The final row is what is
 *  read live at the moment of scoring, placed at today.
 * ──────────────────────────────────────────────────────────────────────────── */

interface Row {
  key: string;
  title: string;
  register: string;
  from: number;
  to: number;
  /** The years printed under the title — "2014–2025", "2024 → 2034". */
  years: string;
  fact: string;
  factor: string | null;
  score: number | null;
}

const NBSP = " ";

function rows(c: PreviewCapture, nl: boolean): Row[] {
  const e = c.evidence;
  const out: Row[] = [];
  const pct = (n: number) => `${n > 0 ? "+" : ""}${n.toLocaleString(nl ? "nl-NL" : "en-GB", { maximumFractionDigits: 1 })}%`;
  const span = (a: number, b: number) => `${a}–${b}`;

  if (e.market) {
    out.push({
      key: "market",
      title: nl ? "Verkoopprijzen Amsterdam" : "Amsterdam sale prices",
      register: nl ? "CBS · €/m², één punt per jaar" : "CBS · €/m², one point a year",
      from: e.market.from, to: e.market.to, years: span(e.market.from, e.market.to),
      fact: nl ? `${e.market.points} jaarlijkse punten` : `${e.market.points} yearly points`,
      factor: "Market Trend", score: e.market.score,
    });
  }
  if (e.woz) {
    out.push({
      key: "woz",
      title: nl ? "WOZ van deze woning" : "This unit's WOZ",
      register: nl ? "Gemeente · jaarlijks getaxeerd" : "Gemeente · assessed yearly",
      from: e.woz.from, to: e.woz.to, years: span(e.woz.from, e.woz.to),
      fact: nl ? `${e.woz.points} taxaties · ${pct(e.woz.cagrPct)}/jr` : `${e.woz.points} assessments · ${pct(e.woz.cagrPct)}/yr`,
      factor: "Value Growth", score: e.woz.score,
    });
  }
  if (e.solar) {
    out.push({
      key: "solar",
      title: nl ? "Zoninstraling per satelliet" : "Satellite irradiance",
      register: "PVGIS · SARAH-3",
      from: e.solar.from, to: e.solar.to, years: span(e.solar.from, e.solar.to),
      fact: nl ? `${e.solar.years} jaar · ${e.solar.yieldKwhPerKwp.toLocaleString("nl-NL")} kWh/kWp` : `${e.solar.years} years · ${e.solar.yieldKwhPerKwp.toLocaleString("en-GB")} kWh/kWp`,
      factor: "Solar Potential", score: e.solar.score,
    });
  }
  if (e.groundMotion?.period) {
    const m = /(\d{4})\D+(\d{4})/.exec(e.groundMotion.period);
    if (m) {
      const mm = e.groundMotion.mmPerYear.toLocaleString(nl ? "nl-NL" : "en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      out.push({
        key: "ground",
        title: nl ? "Bodembeweging" : "Ground motion",
        register: "Copernicus EGMS · Sentinel-1",
        from: +m[1], to: +m[2], years: span(+m[1], +m[2]),
        fact: nl ? `${mm} mm/jaar op dit adres` : `${mm} mm/yr at this address`,
        factor: "Ground Motion", score: e.groundMotion.score,
      });
    }
  }
  if (e.crime) {
    const months = e.crime.months != null ? (nl ? ` + ${e.crime.months} maanden` : ` + ${e.crime.months} months`) : "";
    out.push({
      key: "crime",
      title: nl ? "Criminaliteit, deze buurt" : "Crime, this buurt",
      register: "CBS · Politie",
      from: e.crime.from, to: e.crime.to, years: span(e.crime.from, e.crime.to),
      fact: nl ? `${e.crime.points} jaar${months}` : `${e.crime.points} years${months}`,
      factor: "Crime Trend", score: e.crime.score,
    });
  }
  if (e.energy?.registered && e.energy.validUntil) {
    out.push({
      key: "energy",
      title: nl ? "Energielabel" : "Energy label",
      register: "EP-Online · RVO",
      from: e.energy.registered, to: e.energy.validUntil, years: `${e.energy.registered} → ${e.energy.validUntil}`,
      fact: nl ? `Label ${e.energy.label} · geldig tot ${e.energy.validUntil}` : `Label ${e.energy.label} · valid to ${e.energy.validUntil}`,
      factor: "Energy Label", score: e.energy.score,
    });
  }
  if (e.livability) {
    out.push({
      key: "lbm",
      title: "Leefbaarometer",
      register: e.livability.scale === "buurt"
        ? (nl ? "BZK · 3.0 · buurt" : "BZK · 3.0 · buurt")
        : (nl ? "BZK · 3.0 · 100 m-raster" : "BZK · 3.0 · 100 m grid"),
      from: e.livability.vintage, to: e.livability.vintage, years: `${e.livability.vintage}`,
      // The published class, and the area it was measured over — the same
      // fact the panel prints, so the page and the product cannot disagree.
      fact: e.livability.class9 != null
        ? (nl ? `klasse ${e.livability.class9}/9 voor dit raster` : `class ${e.livability.class9}/9 for this cell`)
        : (nl ? `${e.livability.score5}/5 voor dit raster` : `${e.livability.score5}/5 for this cell`),
      factor: "Livability", score: e.livability.score,
    });
  }
  if (e.populationForecast) {
    const today = parseInt(c.capturedAt.slice(0, 4), 10);
    out.push({
      key: "pop",
      title: nl ? "Bevolkingsprognose" : "Population forecast",
      register: "CBS",
      from: today, to: e.populationForecast.year, years: `→ ${e.populationForecast.year}`,
      fact: nl ? `${pct(e.populationForecast.growthPct)} tot ${e.populationForecast.year}` : `${pct(e.populationForecast.growthPct)} by ${e.populationForecast.year}`,
      factor: "Pop. Forecast", score: e.populationForecast.score,
    });
  }
  return out;
}

/** The chips of the "read live" row — what the moment of scoring itself adds. */
function liveChips(c: PreviewCapture, nl: boolean): string[] {
  const e = c.evidence;
  const chips: string[] = [];
  if (e.viewshed?.buildings) chips.push(nl ? `zichtveld door ${e.viewshed.buildings.toLocaleString("nl-NL")} gebouwen` : `a viewshed through ${e.viewshed.buildings.toLocaleString("en-GB")} buildings`);
  if (e.streetTrees) chips.push(nl ? `${e.streetTrees.count} bomen binnen ${e.streetTrees.radiusM} m` : `${e.streetTrees.count} trees within ${e.streetTrees.radiusM} m`);
  if (e.transitStops) chips.push(nl ? `${e.transitStops} OV-haltes` : `${e.transitStops} transit stops`);
  if (e.wkpb != null) chips.push(nl ? `${e.wkpb} beperkingen op het object` : `${e.wkpb} restrictions on the title`);
  chips.push(nl ? "perceel en bouwvlak" : "the parcel and the building envelope");
  return chips;
}

export default function MeasuredTimeline({ capture, nl }: { capture: PreviewCapture; nl: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const data = rows(capture, nl);
  const today = parseInt(capture.capturedAt.slice(0, 4), 10);
  const first = Math.floor(Math.min(...data.map((r) => r.from), today) / 5) * 5;
  const last = Math.ceil(Math.max(...data.map((r) => r.to), today) / 5) * 5;
  const x = (year: number) => ((year - first) / (last - first)) * 100;
  const ticks: number[] = [];
  for (let y = first; y <= last; y += 5) ticks.push(y);
  const chips = liveChips(capture, nl);

  return (
    <div ref={ref} className="w-full">
      {/* ── Axis head: the origin marker and the year ticks ── */}
      <div className="grid gap-x-6 lg:grid-cols-[240px_minmax(0,1fr)_240px] items-end mb-3">
        <p className="font-data text-[0.62rem] uppercase tracking-[0.14em] text-ink-muted hidden lg:block">
          {capture.built ? (nl ? `Gebouwd ${capture.built} · BAG` : `Built ${capture.built} · BAG`) : ""}
        </p>
        <div className="relative h-5">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute -translate-x-1/2 font-data text-[0.6rem] tabular-nums text-ink-muted"
              style={{ left: `${x(t)}%` }}
            >
              {t}
            </span>
          ))}
        </div>
        <p className="hidden lg:block font-data text-[0.62rem] uppercase tracking-[0.14em] text-ink-muted text-right">
          {nl ? "In de score als" : "In the score as"}
        </p>
      </div>

      {/* ── Rows ── */}
      <ol className="border-t border-edge">
        {data.map((r, i) => (
          <li key={r.key} className="grid gap-x-6 gap-y-2 lg:grid-cols-[240px_minmax(0,1fr)_240px] items-center py-3.5 border-b border-edge">
            <div className="min-w-0">
              <p className="text-[0.92rem] text-ink leading-snug">{r.title}</p>
              <p className="font-data text-[0.6rem] uppercase tracking-[0.12em] text-ink-muted mt-0.5 leading-relaxed">
                {r.years}
                <span aria-hidden className="mx-1.5">·</span>
                {r.register}
              </p>
            </div>
            <Track r={r} x={x} today={today} first={first} last={last} ticks={ticks} inView={inView} delay={i * 0.06} />
            <div className="flex lg:flex-col lg:items-end gap-x-3 gap-y-0.5 items-baseline min-w-0">
              <p className="text-[0.85rem] text-ink-2 leading-snug lg:text-right">{r.fact}</p>
              {r.factor && r.score != null && (
                <p className="font-data text-[0.62rem] uppercase tracking-[0.12em] whitespace-nowrap" style={{ color: scoreInk(r.score) }}>
                  {r.factor}
                  {NBSP}
                  <span className="tabular-nums">{r.score}</span>
                </p>
              )}
            </div>
          </li>
        ))}

        {/* The live row: a dot at today, and what the scan itself adds. */}
        <li className="grid gap-x-6 gap-y-2 lg:grid-cols-[240px_minmax(0,1fr)_240px] items-center py-3.5 border-b border-edge">
          <div className="min-w-0">
            <p className="text-[0.92rem] text-ink leading-snug">{nl ? "Live gelezen, vandaag" : "Read live, today"}</p>
            <p className="font-data text-[0.6rem] uppercase tracking-[0.12em] text-ink-muted mt-0.5 leading-relaxed">
              BRK · 3DBAG · BGT · DSO · WKPB · OSM
            </p>
          </div>
          <div className="relative h-7">
            <Grid ticks={ticks} x={x} today={today} />
            <motion.span
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-accent shadow-[0_0_0_4px_rgb(var(--c-accent)/0.25)]"
              style={{ left: `${x(today)}%` }}
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.35, delay: data.length * 0.06 + 0.2 }}
            />
          </div>
          <ul className="flex flex-wrap gap-1.5 lg:col-start-2 lg:col-span-2 lg:justify-end">
            {chips.map((t) => (
              <li key={t} className="rounded-md border border-edge bg-surface px-2 py-0.5 text-[0.72rem] text-ink-2 leading-snug">
                {t}
              </li>
            ))}
          </ul>
        </li>
      </ol>
      <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-data text-[0.6rem] uppercase tracking-[0.14em] text-ink-muted">
        <span className="inline-flex items-center gap-1.5"><i aria-hidden className="inline-block w-4 h-1.5 rounded-sm bg-accent/70" />{nl ? "gemeten" : "measured"}</span>
        <span className="inline-flex items-center gap-1.5"><i aria-hidden className="inline-block w-4 h-1.5 rounded-sm hatched" />{nl ? "geldig of voorspeld, na vandaag" : "valid or projected, after today"}</span>
        <span className="inline-flex items-center gap-1.5"><i aria-hidden className="inline-block w-px h-3 bg-ink-muted" />{nl ? "vandaag" : "today"}</span>
      </p>
      <style dangerouslySetInnerHTML={{ __html: HATCH_CSS }} />
    </div>
  );
}

/** The hatch for the part of a window that lies after the capture date. */
const HATCH_CSS = `
.hatched{background:repeating-linear-gradient(135deg,rgb(var(--c-accent)/0.55) 0 3px,rgb(var(--c-accent)/0.12) 3px 7px)}
`;

function Grid({ ticks, x, today }: { ticks: number[]; x: (y: number) => number; today: number }) {
  return (
    <>
      {ticks.map((t) => (
        <span key={t} aria-hidden className="absolute top-0 bottom-0 w-px bg-edge" style={{ left: `${x(t)}%` }} />
      ))}
      <span aria-hidden className="absolute top-0 bottom-0 w-px bg-ink-muted/70" style={{ left: `${x(today)}%` }} />
    </>
  );
}

function Track({
  r, x, today, ticks, inView, delay,
}: {
  r: Row; x: (y: number) => number; today: number; first: number; last: number; ticks: number[]; inView: boolean; delay: number;
}) {
  const point = r.from === r.to;
  const measuredTo = Math.min(r.to, today);
  const hasMeasured = r.from < measuredTo;
  const hasFuture = r.to > today;
  const futureFrom = Math.max(r.from, today);
  return (
    <div className="relative h-7">
      <Grid ticks={ticks} x={x} today={today} />
      {point ? (
        <motion.span
          aria-hidden
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-accent/80"
          style={{ left: `${x(r.from)}%` }}
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 0.35, delay: delay + 0.2 }}
        />
      ) : (
        <>
          {hasMeasured && (
            <motion.span
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 h-2.5 rounded-sm bg-accent/70"
              style={{ left: `${x(r.from)}%`, width: `${x(measuredTo) - x(r.from)}%`, transformOrigin: "left center" }}
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1], delay }}
            />
          )}
          {hasFuture && (
            <motion.span
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 h-2.5 rounded-sm hatched"
              style={{ left: `${x(futureFrom)}%`, width: `${x(r.to) - x(futureFrom)}%`, transformOrigin: "left center" }}
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1], delay: delay + 0.35 }}
            />
          )}
        </>
      )}
    </div>
  );
}
